import React from "react";
import {
  Fish,
  LocateFixed,
  Lock,
  MapPin,
  Ruler,
  Scale,
  Waves,
} from "lucide-react";

const GEAR_ICON_PATHS = {
  braidToLeader: "/icons/gear/braid-to-leader.png",
  hook: "/icons/gear/hook.png",
  knots: "/icons/gear/knots.png",
  leader: "/icons/gear/leader.png",
  leaderToHookLure: "/icons/gear/leader-to-hook-lure.png",
  line: "/icons/gear/line-braid.png",
  lure: "/icons/gear/lure.png",
  reel: "/icons/gear/reel.png",
  rod: "/icons/gear/rod.png",
};

function GearIcon({ type }) {
  const iconPath = GEAR_ICON_PATHS[type];
  if (!iconPath) return null;

  return (
    <img
      src={iconPath}
      alt=""
      className="info-popup-gear-icon-img"
      aria-hidden="true"
    />
  );
}

export default function InfoPopupDetailsSection({ infoPopup }) {
  const {
    t,
    data,
    effective,
    isPrivateLocation,
    enableFishipediaFishLink,
    fishipediaNotice,
    openFishipediaForFish,
    fishingMethod,
    usesLure,
    lureInfo,
    hookInfo,
    videoDuration,
    videoDevice,
    openMapAtLocation,
    locationLabel,
    fishSpeciesDisplayName,
  } = infoPopup;

  const fishSpeciesValue = fishSpeciesDisplayName || data.fish;

  const hasCatchDetailSummary =
    Boolean(data.fish) ||
    Boolean(data.weight) ||
    Boolean(data.size) ||
    Boolean(fishingMethod);

  const hasLocationSummary =
    Boolean(isPrivateLocation) ||
    Boolean(!isPrivateLocation && data.coordinates);

  const hasGearRigSummary =
    Boolean(effective.rod) ||
    Boolean(effective.reel) ||
    Boolean(effective.braid) ||
    Boolean(effective.leader) ||
    Boolean(lureInfo) ||
    Boolean(effective.lure) ||
    Boolean(hookInfo) ||
    Boolean(effective.knotBraidToLeader) ||
    Boolean(effective.knotLeaderToLure);

  const renderDetailCard = (Icon, label, value, extraClassName = "") => (
    <div className={`info-popup-detail-card ${extraClassName}`.trim()}>
      <span className="info-popup-detail-card-icon" aria-hidden="true">
        <Icon size={16} strokeWidth={2.25} />
      </span>
      <span className="info-popup-detail-card-copy">
        <span className="info-popup-detail-card-label">{label}</span>
        <strong>{value}</strong>
      </span>
    </div>
  );

  const renderGearDetailCard = (iconType, label, value) =>
    renderDetailCard(() => <GearIcon type={iconType} />, label, value);

  const renderStackedRow = (Icon, label, value) => (
    <div className="info-popup-stacked-row">
      <span className="info-popup-stacked-row-icon" aria-hidden="true">
        <Icon size={16} strokeWidth={2.25} />
      </span>
      <span className="info-popup-stacked-row-copy">
        <span className="info-popup-stacked-row-label">{label}</span>
        <strong>{value}</strong>
      </span>
    </div>
  );

  return (
    <>
      {hasCatchDetailSummary && (
        <section className="info-popup-section info-popup-section--catch-details">
          <h4 className="info-popup-section-title">
            {t("info_popup_section_catch_details")}
          </h4>
          <div className="info-popup-detail-grid">
            {data.fish &&
              renderDetailCard(
                Fish,
                t("info_popup_fish_species"),
                enableFishipediaFishLink ? (
                  <button
                    type="button"
                    className="info-popup-fish-link"
                    onClick={openFishipediaForFish}
                  >
                    {fishSpeciesValue}
                  </button>
                ) : (
                  fishSpeciesValue
                ),
                "info-popup-detail-card--fish"
              )}

            {data.weight && renderDetailCard(Scale, t("weight"), data.weight)}

            {data.size && renderDetailCard(Ruler, t("size"), data.size)}

            {fishingMethod &&
              renderDetailCard(
                Waves,
                t("info_popup_method_used"),
                fishingMethod,
                "info-popup-detail-card--method"
              )}
          </div>

          {fishipediaNotice ? (
            <div key={fishipediaNotice.id} className="info-popup-notice">
              {fishipediaNotice.message}
            </div>
          ) : null}
        </section>
      )}

      {hasLocationSummary && (
        <section className="info-popup-section info-popup-section--location">
          <h4 className="info-popup-section-title">
            {t("info_popup_section_coordinates_location")}
          </h4>
          <div className="info-popup-stacked-card">
            {isPrivateLocation ? (
              renderStackedRow(Lock, t("location"), t("location_hidden"))
            ) : (
              <>
                {data.coordinates &&
                  renderStackedRow(
                    LocateFixed,
                    t("coordinates"),
                    <span
                      onClick={openMapAtLocation}
                      className="info-popup-coordinates"
                    >
                      {data.coordinates}
                    </span>
                  )}

                {data.coordinates &&
                  locationLabel &&
                  renderStackedRow(MapPin, t("location"), locationLabel)}
              </>
            )}
          </div>
        </section>
      )}

      {hasGearRigSummary && (
        <section className="info-popup-section info-popup-section--gear">
          <h4 className="info-popup-section-title">
            {t("info_popup_section_gear_rig")}
          </h4>
          <div className="info-popup-detail-grid info-popup-detail-grid--gear">
            {effective.rod &&
              renderGearDetailCard("rod", t("rod"), effective.rod)}

            {effective.reel &&
              renderGearDetailCard("reel", t("reel"), effective.reel)}

            {effective.braid &&
              renderGearDetailCard(
                "line",
                t("gear_braid_mono"),
                effective.braid
              )}

            {effective.leader &&
              renderGearDetailCard("leader", t("leader"), effective.leader)}

            {usesLure ? (
              lureInfo && renderGearDetailCard("lure", t("lure"), lureInfo)
            ) : (
              <>
                {effective.lure &&
                  renderGearDetailCard("lure", t("bait"), effective.lure)}
                {hookInfo && renderGearDetailCard("hook", t("hook"), hookInfo)}
              </>
            )}

            {effective.knotBraidToLeader &&
              renderGearDetailCard(
                "braidToLeader",
                t("knot_braid_leader"),
                effective.knotBraidToLeader
              )}

            {effective.knotLeaderToLure &&
              renderGearDetailCard(
                "leaderToHookLure",
                t("knot_leader_lure"),
                effective.knotLeaderToLure
              )}
          </div>
        </section>
      )}

      {videoDuration && (
        <p>
          <strong>{t("duration", { defaultValue: "Duration" })}:</strong>{" "}
          {videoDuration}
        </p>
      )}
      {videoDevice && (
        <p>
          <strong>{t("device", { defaultValue: "Device" })}:</strong>{" "}
          {videoDevice}
        </p>
      )}
    </>
  );
}
